/**
 * Lightweight client-side "calling queue" system.
 *
 * The Candidates list page persists the ordered list of candidate IDs
 * currently visible (after filters + search) into localStorage. The
 * Scorecard page reads it to provide prev/next navigation while the
 * recruiter goes through their batch of calls.
 *
 * No backend involved — pure UI continuity.
 */
const KEY = "prerec.call_queue";

/**
 * @param {Object} payload
 * @param {string[]} payload.ids       Ordered list of candidate IDs.
 * @param {string}   payload.label     Human-readable label of the filter (e.g. "À rappeler").
 * @param {string}   [payload.return_url] URL to go back to (defaults to /candidats).
 */
export function saveQueue({ ids, label, return_url }) {
    try {
        localStorage.setItem(
            KEY,
            JSON.stringify({
                ids,
                label,
                return_url: return_url || "/candidats",
                saved_at: Date.now(),
            }),
        );
    } catch {
        /* ignore quota errors */
    }
}

export function readQueue() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        // Drop stale queues older than 6 hours
        if (!data?.ids?.length || (Date.now() - (data.saved_at || 0)) > 6 * 3600 * 1000) {
            localStorage.removeItem(KEY);
            return null;
        }
        return data;
    } catch {
        return null;
    }
}

export function clearQueue() {
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/**
 * Drop a candidate from the persisted queue. Use this when the candidate
 * has been moved to a state that takes them out of the active calling list
 * (talent pool, rejected, etc.) so that prev/next navigation skips over
 * them and the displayed "position N / M" stays consistent.
 *
 * Returns the queue entry adjacent to the removed id (nextId preferred,
 * else prevId) so the caller can navigate to it; null if no neighbour
 * exists or the candidate wasn't in the queue.
 */
export function removeFromQueue(candidateId) {
    try {
        const queue = readQueue();
        if (!queue) return null;
        const idx = queue.ids.indexOf(candidateId);
        if (idx === -1) return null;
        const newIds = queue.ids.filter((x) => x !== candidateId);
        // Pick the next neighbour (or previous if we just removed the last one)
        const neighbourId = newIds[idx] || newIds[idx - 1] || null;
        if (newIds.length === 0) {
            localStorage.removeItem(KEY);
        } else {
            localStorage.setItem(KEY, JSON.stringify({ ...queue, ids: newIds, saved_at: Date.now() }));
        }
        return neighbourId;
    } catch {
        return null;
    }
}

/**
 * Returns navigation info for a given candidate id.
 * @returns {{position: number, total: number, prevId: string|null, nextId: string|null, label: string, return_url: string} | null}
 */
export function getQueuePosition(currentId) {
    const queue = readQueue();
    if (!queue) return null;
    const idx = queue.ids.indexOf(currentId);
    if (idx === -1) return null;
    return {
        position: idx + 1,
        total: queue.ids.length,
        prevId: idx > 0 ? queue.ids[idx - 1] : null,
        nextId: idx < queue.ids.length - 1 ? queue.ids[idx + 1] : null,
        allIds: queue.ids,
        label: queue.label,
        return_url: queue.return_url,
    };
}
